import React, { useEffect, useRef, useCallback, useState } from 'react';
import { Link } from 'react-router-dom';
import './LandingPage.css';

const CLIENT_APP = process.env.REACT_APP_CLIENT_URL || 'https://client.shynea.one';
const PRESTATION_APP = process.env.REACT_APP_PRESTATION_URL || 'https://prestation.shynea.one';
const CONTACT_EMAIL = 'contact@shynea.one';

const HERO_IMG = 'https://images.unsplash.com/photo-1560066984-138dadb4c035?w=800&q=80';
const CLIENT_SALON_IMG = 'https://images.unsplash.com/photo-1633681926022-84c23e8cb2d6?w=800&q=80';
const CONTACT_WOMAN_IMG = 'https://images.unsplash.com/photo-1594744803329-e58b31de8bf5?w=600&q=80';

function useReveal() {
  const ref = useRef(null);
  const observe = useCallback(() => {
    if (!ref.current) return;
    const els = ref.current.querySelectorAll('.v-reveal, .v-reveal-left, .v-reveal-right, .v-reveal-scale');
    const io = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) { e.target.classList.add('visible'); io.unobserve(e.target); } }),
      { threshold: 0.12, rootMargin: '0px 0px -40px 0px' }
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);
  useEffect(() => { const cleanup = observe(); return cleanup; }, [observe]);
  return ref;
}

function useCountUp(end, suffix = '', duration = 1800) {
  const [val, setVal] = useState('0' + suffix);
  const ref = useRef(null);
  const started = useRef(false);

  useEffect(() => {
    if (!ref.current) return;
    const io = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !started.current) {
        started.current = true;
        const start = 0;
        const startTime = performance.now();
        const tick = (now) => {
          const elapsed = now - startTime;
          const progress = Math.min(elapsed / duration, 1);
          const eased = 1 - Math.pow(1 - progress, 3);
          const current = Math.round(start + (end - start) * eased);
          setVal(current + suffix);
          if (progress < 1) requestAnimationFrame(tick);
        };
        requestAnimationFrame(tick);
        io.unobserve(entry.target);
      }
    }, { threshold: 0.3 });
    io.observe(ref.current);
    return () => io.disconnect();
  }, [end, suffix, duration]);

  return [ref, val];
}

function useBackToTop() {
  const [show, setShow] = useState(false);
  useEffect(() => {
    const onScroll = () => setShow(window.scrollY > 400);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  return show;
}

function LandingPage() {
  const rootRef = useReveal();
  const showBackTop = useBackToTop();
  const [menuOpen, setMenuOpen] = useState(false);
  const [toast, setToast] = useState(null);
  const [sending, setSending] = useState(false);

  const [salonsRef, salonsVal] = useCountUp(20, '+');
  const [clicsRef, clicsVal] = useCountUp(3, '');
  const [commRef, commVal] = useCountUp(0, '%');

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 4000);
  };

  const closeMenu = () => setMenuOpen(false);

  return (
    <div className="v" ref={rootRef}>
      {/* ════════ TOAST ════════ */}
      {toast && (
        <div className={`v-toast v-toast-${toast.type}`}>
          <span>{toast.type === 'success' ? '✓' : '✕'}</span> {toast.msg}
        </div>
      )}

      {/* ════════ NAV ════════ */}
      <nav className="v-nav">
        <div className="v-nav-inner">
          <a href="#top" className="v-nav-logo">Shynea</a>
          <button className={`v-burger${menuOpen ? ' open' : ''}`} onClick={() => setMenuOpen(!menuOpen)} aria-label="Menu">
            <span /><span /><span />
          </button>
          <div className={`v-nav-links${menuOpen ? ' open' : ''}`}>
            <a href="#pour-salons" onClick={closeMenu}>Salons</a>
            <a href="#pour-clients" onClick={closeMenu}>Clients</a>
            <a href="#apropos" onClick={closeMenu}>À propos</a>
            <a href="#carrieres" onClick={closeMenu}>Carrières</a>
            <a href="#contact" onClick={closeMenu}>Contact</a>
          </div>
          <div className="v-nav-ctas">
            <a className="v-btn v-btn-outline" href={CLIENT_APP}>Je réserve</a>
            <a className="v-btn v-btn-dark" href={PRESTATION_APP}>Rejoindre en tant que salon</a>
          </div>
        </div>
      </nav>
      {menuOpen && <div className="v-overlay" onClick={closeMenu} />}

      {/* ════════ HERO ════════ */}
      <section id="top" className="v-section" style={{ paddingBottom: 0 }}>
        <div className="v-container">
          <div className="v-hero">
            <div className="v-reveal-left">
              <div className="v-hero-tags">
                <span className="v-tag v-tag-dark">Lancement · Sénégal 2026</span>
                <span className="v-tag v-tag-green">Pré-inscription ouverte</span>
              </div>
              <h1 className="serif">
                La plateforme premium<br />
                qui fait <span className="accent">gagner de<br />l&apos;argent</span> aux salons
              </h1>
              <p className="v-hero-desc">
                Shynea connecte les meilleurs salons beauté d&apos;Afrique à une clientèle qualifiée
                — avec des outils pensés pour automatiser, encaisser et fidéliser.
              </p>
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                <a className="v-btn v-btn-dark" href={PRESTATION_APP}>Rejoindre en tant que salon &rarr;</a>
                <a className="v-btn v-btn-outline" href={CLIENT_APP}>Réserver une prestation</a>
              </div>
            </div>
            <div className="v-reveal-right v-hero-img-wrap">
              <img src={HERO_IMG} alt="Salon de beauté" />
              <div className="v-hero-notif">
                <div className="v-hero-notif-check">
                  <svg width="18" height="18" viewBox="0 0 18 18" fill="none"><path d="M4 9l3.5 3.5L14 5" stroke="#16a34a" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                </div>
                <div>
                  <p>Réservation confirmée</p>
                  <span>Il y a 2 minutes</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ════════ STATS ════════ */}
      <section className="v-section-sm">
        <div className="v-container v-reveal">
          <div className="v-stats">
            <div className="v-stat" ref={salonsRef}>
              <p className="v-stat-num serif">{salonsVal}</p>
              <p className="v-stat-label">salons partenaires</p>
            </div>
            <div className="v-stat-sep" />
            <div className="v-stat" ref={clicsRef}>
              <p className="v-stat-num serif">{clicsVal}</p>
              <p className="v-stat-label">clics pour réserver</p>
            </div>
            <div className="v-stat-sep" />
            <div className="v-stat" ref={commRef}>
              <p className="v-stat-num serif">{commVal}</p>
              <p className="v-stat-label">commission cachée</p>
            </div>
            <div className="v-stat-sep" />
            <div className="v-stat">
              <p className="v-stat-num serif" style={{ fontSize: 22 }}>Wave &amp; Orange Money</p>
              <p className="v-stat-label">intégrés</p>
            </div>
          </div>
        </div>
      </section>
      <div className="v-divider" style={{ maxWidth: 1260, margin: '0 auto' }} />

      {/* ════════ POUR LES SALONS ════════ */}
      <section id="pour-salons" className="v-section">
        <div className="v-container">
          <div className="v-two-col">
            <div className="v-reveal-left">
              <span className="v-label v-label-orange">Pour les salons</span>
              <h2 className="v-h2">
                Augmentez votre chiffre<br />d&apos;affaires <span className="accent-orange">sans effort</span>
              </h2>
              <p className="v-subtitle">
                Votre lien Instagram suffit. Shynea s&apos;occupe des réservations, des acomptes, des rappels — vous
                vous concentrez sur votre métier.
              </p>
              <div className="v-features">
                <FeatureItem icon="📈" title="Plus de clients automatiquement" desc="via votre page de réservation dédiée" color="orange" />
                <FeatureItem icon="📅" title="Gestion des RDV centralisée" desc="agenda, annulations, rappels automatiques" color="orange" />
                <FeatureItem icon="💳" title="Paiements intégrés" desc="Wave, Orange Money, acompte 30% automatique" color="orange" />
                <FeatureItem icon="⭐" title="Visibilité premium" desc="auprès d'une clientèle qui recherche la qualité" color="orange" />
                <FeatureItem icon="📊" title="Tableau de bord de pilotage" desc="revenus, taux de remplissage, clients récurrents" color="orange" />
              </div>
              <a className="v-btn v-btn-dark" href={PRESTATION_APP} style={{ marginTop: 24 }}>Rejoindre Shynea &rarr;</a>
            </div>
            <div className="v-reveal-right">
              <div className="v-card" style={{ marginBottom: 16 }}>
                <p style={{ fontSize: 12, color: 'var(--text-muted)', fontWeight: 600, marginBottom: 14, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Votre tableau de bord</p>
                <div className="v-dash">
                  <div className="v-dash-box">
                    <p className="label">RDV ce mois</p>
                    <p className="val serif">48</p>
                  </div>
                  <div className="v-dash-box">
                    <p className="label">Revenus</p>
                    <p className="val val-orange serif">186k</p>
                  </div>
                </div>
                <div className="v-dash-next">
                  <div>
                    <p style={{ fontSize: 11, color: 'var(--text-muted)' }}>Prochain RDV</p>
                    <p style={{ fontSize: 14, fontWeight: 600, marginTop: 3 }}>Aminata D.</p>
                    <p style={{ fontSize: 12, color: 'var(--text-muted)' }}>14h30</p>
                  </div>
                  <span className="v-badge-green">Confirmé</span>
                </div>
              </div>
              <div className="v-testimonial">
                <p>
                  &quot;Avant Shynea, je perdais des heures en appels et messages. Maintenant mes clientes réservent
                  seules et paient à l&apos;avance. Mon agenda est plein.&quot;
                </p>
                <div className="v-testimonial-author">
                  <div className="v-testimonial-avatar">KD</div>
                  <div>
                    <p className="name">Khadija D.</p>
                    <p className="role">Salon Élégance · Dakar</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="v-divider" style={{ maxWidth: 1260, margin: '0 auto' }} />

      {/* ════════ POUR LES CLIENTS ════════ */}
      <section id="pour-clients" className="v-section">
        <div className="v-container">
          <div className="v-two-col">
            <div className="v-photo v-reveal-left">
              <img src={CLIENT_SALON_IMG} alt="Intérieur salon" />
            </div>
            <div className="v-reveal-right">
              <span className="v-label v-label-blue">Pour les clients</span>
              <h2 className="v-h2">
                Les meilleurs salons, réservés<br />en <span className="accent-blue">quelques secondes</span>
              </h2>
              <p className="v-subtitle">
                Une sélection rigoureuse de salons premium. Vous choisissez, vous payez avec Mobile Money, c&apos;est confirmé.
              </p>
              <div className="v-features">
                <FeatureItem icon="⚡" title="Réservation instantanée" desc="depuis le lien bio de votre salon préféré" color="blue" />
                <FeatureItem icon="✓" title="Salons vérifiés" desc="sélectionnés par l'équipe Shynea" color="blue" />
                <FeatureItem icon="🔒" title="Paiement sécurisé" desc="Wave · Orange Money · aucune CB nécessaire" color="blue" />
                <FeatureItem icon="🔔" title="Rappels automatiques" desc="avant votre rendez-vous" color="blue" />
              </div>
              <a className="v-btn v-btn-blue" href={CLIENT_APP} style={{ marginTop: 24 }}>Réserver maintenant &rarr;</a>
            </div>
          </div>
        </div>
      </section>

      <div className="v-divider" style={{ maxWidth: 1260, margin: '0 auto' }} />

      {/* ════════ POURQUOI SHYNEA ════════ */}
      <section className="v-section v-center">
        <div className="v-container">
          <div className="v-reveal">
            <h2 className="v-h2 serif" style={{ marginBottom: 8 }}>Pourquoi Shynea</h2>
            <p style={{ fontSize: 14, color: 'var(--text-secondary)', marginBottom: 48 }}>
              Pas une app de plus. <strong>Une plateforme pensée</strong> pour l&apos;Afrique.
            </p>
          </div>
          <div className="v-why-grid">
            <div className="v-why-card v-reveal-scale v-stagger-1">
              <div className="v-why-icon v-why-icon-1">👤</div>
              <h3>Sélection premium</h3>
              <p>Tous les salons ne sont pas sur Shynea. Chaque partenaire est vérifié pour garantir un niveau d&apos;excellence.</p>
            </div>
            <div className="v-why-card v-reveal-scale v-stagger-2">
              <div className="v-why-icon v-why-icon-2">📈</div>
              <h3>Revenus maximisés</h3>
              <p>Acomptes automatiques, zéro no-show, encaissement Mobile Money. Votre trésorerie est protégée dès le premier clic.</p>
            </div>
            <div className="v-why-card v-reveal-scale v-stagger-3">
              <div className="v-why-icon v-why-icon-3">✨</div>
              <h3>Simplicité extrême</h3>
              <p>Un lien dans votre bio Instagram. Les clientes réservent et paient seules, 24h/24, sans appel téléphonique.</p>
            </div>
            <div className="v-why-card v-reveal-scale v-stagger-4">
              <div className="v-why-icon v-why-icon-4">🌍</div>
              <h3>Pensé pour l&apos;Afrique</h3>
              <p>Mobile Money natif, interface légère, conçu pour fonctionner sur tous les réseaux. Pas un produit importé adapté.</p>
            </div>
          </div>
        </div>
      </section>

      <div className="v-divider" style={{ maxWidth: 1260, margin: '0 auto' }} />

      {/* ════════ LE MOT DU DIRECTEUR ════════ */}
      <section className="v-section-alt v-center">
        <div className="v-container">
          <h2 className="v-h2 serif v-reveal" style={{ marginBottom: 40 }}>Le mot du CEO</h2>
          <div className="v-director-card v-reveal-scale" style={{ textAlign: 'left' }}>
            <div className="v-quote-mark">&ldquo;</div>
            <p className="text">
              Nous avons créé Shynea parce que les salons africains méritaient mieux que des outils génériques
              conçus ailleurs. La beauté est une industrie sérieuse ici — elle doit être prise au sérieux.
            </p>
            <p className="text">
              Le marché de la beauté au Sénégal est vibrant, professionnel, ambitieux. Nos salons partenaires sont
              de vraies entreprises — ils gèrent des équipes, des stocks, des clientes fidèles. Ils méritaient une
              plateforme qui parle leur langue, intègre leurs outils de paiement, et leur donne une visibilité à la
              hauteur de leur talent.
            </p>
            <p className="text-bold">
              Shynea n&apos;est pas un annuaire. C&apos;est un partenaire de croissance. Notre succès se mesure au vôtre.
            </p>
            <hr />
            <div className="v-director-info">
              <div className="v-director-avatar">S</div>
              <div>
                <p className="name">Omar NDIAYE</p>
                <p className="role">Shynea</p>
              </div>
            </div>
            <p className="v-director-loc">shynea.one · Dakar, Sénégal</p>
          </div>
        </div>
      </section>

      <div className="v-divider" style={{ maxWidth: 1260, margin: '0 auto' }} />

      {/* ════════ QUI SOMMES-NOUS ════════ */}
      <section id="apropos" className="v-section">
        <div className="v-container">
          <div className="v-center v-reveal" style={{ marginBottom: 56 }}>
            <h2 className="v-h2 serif" style={{ fontStyle: 'italic' }}>Qui sommes-nous</h2>
            <p className="v-subtitle" style={{ margin: '0 auto' }}>
              Une startup tech née à Dakar, portée par une conviction : la beauté africaine mérite une infrastructure à sa hauteur.
            </p>
          </div>

          <div className="v-about-story v-two-col" style={{ gap: 60, marginBottom: 64 }}>
            <div className="v-reveal-left">
              <div className="v-about-origin-card">
                <span className="v-about-origin-emoji">💡</span>
                <h3 className="serif" style={{ fontSize: 28, fontWeight: 600, marginBottom: 12 }}>L&apos;origine</h3>
                <p style={{ fontSize: 14, color: 'var(--text-secondary)', lineHeight: 1.75, marginBottom: 14 }}>
                  Shynea est née d&apos;un constat simple : des milliers de salons de beauté talentueux au Sénégal
                  jonglent entre appels WhatsApp, carnets papier et annulations de dernière minute. Pendant ce temps,
                  leurs clients veulent réserver comme ils commandent à manger — en 3 clics.
                </p>
                <p style={{ fontSize: 14, color: 'var(--text-secondary)', lineHeight: 1.75 }}>
                  Aucune plateforme existante ne répondait à cette réalité. Pas de Mobile Money intégré,
                  pas de logique adaptée au terrain, pas de sélection qualitative. Nous avons décidé de
                  construire ce qui manquait.
                </p>
              </div>
            </div>
            <div className="v-reveal-right">
              <div className="v-about-mission-card">
                <span className="v-about-mission-emoji">🎯</span>
                <h3 className="serif" style={{ fontSize: 28, fontWeight: 600, marginBottom: 12, color: '#fff' }}>Notre mission</h3>
                <p style={{ fontSize: 14, color: 'rgba(255,255,255,0.75)', lineHeight: 1.75, marginBottom: 14 }}>
                  Donner à chaque salon de beauté premium en Afrique les outils pour
                  automatiser ses réservations, sécuriser ses revenus et bâtir une
                  clientèle fidèle — sans effort technique.
                </p>
                <p style={{ fontSize: 14, color: 'rgba(255,255,255,0.75)', lineHeight: 1.75 }}>
                  Et offrir aux clientes une expérience de réservation fluide,
                  sécurisée et digne de confiance. Toujours.
                </p>
              </div>
            </div>
          </div>

          <div className="v-about-pillars v-reveal">
            <div className="v-about-pillar v-reveal-scale v-stagger-1">
              <div className="v-about-pillar-icon">🏗️</div>
              <h4>Technologie</h4>
              <p>Plateforme développée de A à Z par notre équipe. React, Node.js, Mobile Money natif, hébergement Dakar.</p>
            </div>
            <div className="v-about-pillar v-reveal-scale v-stagger-2">
              <div className="v-about-pillar-icon">🤝</div>
              <h4>Proximité terrain</h4>
              <p>Chaque fonctionnalité naît de conversations réelles avec des gérants de salons et leurs clientes.</p>
            </div>
            <div className="v-about-pillar v-reveal-scale v-stagger-3">
              <div className="v-about-pillar-icon">🌍</div>
              <h4>Ambition panafricaine</h4>
              <p>Lancés à Dakar, nous visons Abidjan, Bamako, Douala. Un standard premium pour tout le continent.</p>
            </div>
            <div className="v-about-pillar v-reveal-scale v-stagger-4">
              <div className="v-about-pillar-icon">✨</div>
              <h4>Premium, sans élitisme</h4>
              <p>Nous croyons que la qualité n&apos;est pas un luxe. Chaque salon mérite des outils professionnels accessibles.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ════════ NOS VALEURS & VISION (dark) ════════ */}
      <section className="v-section-dark">
        <div className="v-container">
          <div className="v-values-vision-grid">
            <div className="v-reveal-left">
              <h2 className="v-h2 serif" style={{ color: '#fff', marginBottom: 6 }}>Nos valeurs &amp; notre vision</h2>
              <p style={{ fontSize: 14, color: 'rgba(255,255,255,0.55)', marginBottom: 36, maxWidth: 480 }}>
                Ce qui guide chacune de nos décisions et la direction dans laquelle nous allons.
              </p>
              <div className="v-values-list">
                <div className="v-value-item">
                  <div className="v-value-num">1</div>
                  <div>
                    <h4>Excellence sans compromis</h4>
                    <p>Chaque détail compte — du design de l&apos;interface à la qualité des salons sélectionnés. Le standard premium n&apos;est pas négociable.</p>
                  </div>
                </div>
                <div className="v-value-item">
                  <div className="v-value-num">2</div>
                  <div>
                    <h4>Honnêteté commerciale</h4>
                    <p>Pas de frais cachés, pas de promesses vides. Nous gagnons quand nos salons gagnent.</p>
                  </div>
                </div>
                <div className="v-value-item">
                  <div className="v-value-num">3</div>
                  <div>
                    <h4>Ancrage local, vision globale</h4>
                    <p>Conçu à Dakar, pour Dakar d&apos;abord — avec la rigueur d&apos;une startup tech internationale.</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="v-reveal-right">
              <div className="v-vision-card-dark">
                <div className="v-vision-card-dark-icon">🔭</div>
                <h3>Notre vision</h3>
                <p>
                  La beauté africaine est une industrie de plusieurs milliards. Elle souffre pourtant d&apos;un déficit
                  d&apos;infrastructure numérique — pas d&apos;outils pensés pour elle, pas de plateforme qui comprend le
                  Mobile Money, la fidélisation locale, la puissance d&apos;Instagram.
                </p>
                <p>
                  Shynea comble ce vide. Nous construisons quelque chose de nouveau — une marketplace beauté
                  premium-first, mobile-first, africaine-first.
                </p>
                <p className="bold">
                  L&apos;objectif : être la référence incontournable de la beauté premium en Afrique de l&apos;Ouest.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="v-divider" style={{ maxWidth: 1260, margin: '0 auto' }} />

      {/* ════════ CARRIÈRES ════════ */}
      <section id="carrieres" className="v-section v-center">
        <div className="v-container">
          <div className="v-reveal" style={{ marginBottom: 36 }}>
            <h2 className="v-h2 serif" style={{ marginBottom: 6 }}>Carrières</h2>
            <p style={{ fontSize: 15, color: 'var(--text-secondary)', marginBottom: 6 }}>Construisez Shynea avec nous</p>
            <p className="v-subtitle" style={{ maxWidth: 560, margin: '0 auto' }}>
              Nous recrutons des profils qui partagent notre ambition : faire de Shynea la référence beauté de l&apos;Afrique de l&apos;Ouest.
            </p>
          </div>

          <div className="v-careers-list">
            <Link to="/carrieres/technico-commercial" className="v-career-card v-reveal v-stagger-1" style={{ textDecoration: 'none', color: 'inherit' }}>
              <div className="v-career-card-left">
                <div className="v-career-icon">
                  <svg width="18" height="18" viewBox="0 0 18 18" fill="none"><path d="M3 6h12v8a2 2 0 01-2 2H5a2 2 0 01-2-2V6z" stroke="currentColor" strokeWidth="1.5"/><path d="M6 6V4a3 3 0 016 0v2" stroke="currentColor" strokeWidth="1.5"/></svg>
                </div>
                <div style={{ textAlign: 'left' }}>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 6 }}>
                    <span className="v-badge-open-sm">CDD — 6 mois</span>
                    <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>📍 Dakar · Juin 2026</span>
                  </div>
                  <h3>Technico-Commercial Terrain Junior F/H</h3>
                  <p>Prospection terrain, démonstration de l&apos;application, signature de partenariats salons.</p>
                </div>
              </div>
              <span className="v-btn v-btn-dark">Voir l&apos;offre &rarr;</span>
            </Link>
          </div>

          <p className="v-reveal" style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 28 }}>
            Vous ne vous reconnaissez pas dans ces offres mais vous croyez en Shynea ?
          </p>
          <Link to="/carrieres/technico-commercial" className="v-btn v-btn-outline" style={{ marginTop: 10 }}>
            Candidature spontanée →
          </Link>
        </div>
      </section>

      <div className="v-divider" style={{ maxWidth: 1260, margin: '0 auto' }} />

      {/* ════════ CONTACT ════════ */}
      <section id="contact" className="v-section v-center">
        <div className="v-container">
          <div className="v-reveal">
            <h2 className="v-h2 serif" style={{ marginBottom: 6 }}>Contact</h2>
            <p style={{ fontSize: 15, color: 'var(--text-secondary)', marginBottom: 6 }}>Parlons-nous</p>
            <p className="v-subtitle" style={{ maxWidth: 520, margin: '0 auto 40px' }}>
              Vous êtes un salon, un investisseur, un partenaire potentiel ou simplement curieux ? Nous répondons à tous les messages.
            </p>
          </div>
          <div className="v-contact-grid" style={{ textAlign: 'left' }}>
            <div className="v-reveal-left">
              <div className="v-contact-info-card">
                <div className="v-contact-icon v-contact-icon-purple">
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M2 5l8 6 8-6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/><rect x="1" y="4" width="18" height="12" rx="3" stroke="currentColor" strokeWidth="1.5" fill="none"/></svg>
                </div>
                <div>
                  <p className="label">Email</p>
                  <p className="value">{CONTACT_EMAIL}</p>
                </div>
              </div>
              <div className="v-contact-info-card">
                <div className="v-contact-icon v-contact-icon-red">
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M10 2C6.69 2 4 4.69 4 8c0 5 6 10 6 10s6-5 6-10c0-3.31-2.69-6-6-6zm0 8a2 2 0 110-4 2 2 0 010 4z" fill="currentColor"/></svg>
                </div>
                <div>
                  <p className="label">Localisation</p>
                  <p className="value">Dakar, Sénégal</p>
                </div>
              </div>
              <div className="v-contact-photo">
                <img src={CONTACT_WOMAN_IMG} alt="Shynea" />
              </div>
            </div>
            <form
              className="v-form v-reveal-right"
              onSubmit={async (e) => {
                e.preventDefault();
                if (sending) return;
                setSending(true);
                const fd = new FormData(e.target);
                try {
                  const res = await fetch('/api/contact', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                      name: fd.get('name'),
                      email: fd.get('email'),
                      profil: fd.get('profil'),
                      message: fd.get('message'),
                    }),
                  });
                  if (res.ok) {
                    showToast('Message envoyé avec succès !');
                    e.target.reset();
                  } else {
                    showToast('Erreur lors de l\'envoi.', 'error');
                  }
                } catch {
                  showToast('Erreur réseau, réessayez.', 'error');
                }
                setSending(false);
              }}
            >
              <label htmlFor="c-name">Votre nom</label>
              <input id="c-name" name="name" type="text" placeholder="Entrez votre nom" required />
              <label htmlFor="c-email">Votre email</label>
              <input id="c-email" name="email" type="email" placeholder="votre@email.com" required />
              <label htmlFor="c-profil">Je suis</label>
              <select id="c-profil" name="profil" defaultValue="un salon">
                <option>un salon</option>
                <option>un client</option>
                <option>un investisseur / partenaire</option>
                <option>candidature spontanée</option>
                <option>autre</option>
              </select>
              <label htmlFor="c-msg">Votre message</label>
              <textarea id="c-msg" name="message" placeholder="Votre message..." required />
              <button type="submit" className="v-btn v-btn-dark" style={{ width: '100%', justifyContent: 'center' }} disabled={sending}>
                {sending ? 'Envoi en cours...' : <>Envoyer le message &nbsp;
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M14 2L7 9M14 2l-4.5 12L7 9m7-7L2 6.5 7 9" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg></>}
              </button>
            </form>
          </div>
        </div>
      </section>

      {/* ════════ CTA FINAL ════════ */}
      <section className="v-section">
        <div className="v-container">
          <div className="v-cta-banner v-reveal-scale">
            <h2>Prêt à transformer votre salon ?</h2>
            <p>Rejoignez les salons qui ont choisi Shynea pour développer leur activité.</p>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
              <a className="v-btn v-btn-orange" href={PRESTATION_APP}>Rejoindre Shynea &rarr;</a>
              <a className="v-btn v-btn-outline" href={CLIENT_APP} style={{ color: '#fff', borderColor: 'rgba(255,255,255,0.3)' }}>Réserver une prestation</a>
            </div>
          </div>
        </div>
      </section>

      {/* ════════ FOOTER ════════ */}
      <footer className="v-footer">
        <div className="v-container">
          <div className="v-footer-grid">
            <div>
              <p className="v-footer-logo">Shynea</p>
              <p className="v-footer-desc">
                La plateforme premium qui connecte les meilleurs salons beauté d&apos;Afrique à leurs clients.
              </p>
              <p className="v-footer-loc">Dakar, Sénégal · shynea.one</p>
            </div>
            <div>
              <p className="v-footer-col-title">Plateforme</p>
              <a className="v-footer-link" href="#pour-salons">Pour les salons</a>
              <a className="v-footer-link" href="#pour-clients">Pour les clients</a>
              <a className="v-footer-link" href={CLIENT_APP}>Réserver une prestation</a>
              <a className="v-footer-link" href={PRESTATION_APP}>Rejoindre en tant que salon</a>
            </div>
            <div>
              <p className="v-footer-col-title">Entreprise</p>
              <a className="v-footer-link" href="#apropos">À propos</a>
              <a className="v-footer-link" href="#apropos">Notre vision</a>
              <a className="v-footer-link" href="#carrieres">Carrières</a>
              <span className="v-footer-link">Presse</span>
              <a className="v-footer-link" href="#contact">Contact</a>
            </div>
          </div>
          <div className="v-footer-bottom">
            <div className="v-footer-bottom-left">
              <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>
              <span>Mentions légales</span>
              <span>© {new Date().getFullYear()} Shynea · Tous droits réservés</span>
            </div>
            <div className="v-footer-bottom-right">
              <span>Fait avec soin à Dakar</span>
              <div className="v-footer-social" title="Instagram">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="2" width="20" height="20" rx="5"/><circle cx="12" cy="12" r="5"/><circle cx="17.5" cy="6.5" r="1.5" fill="currentColor" stroke="none"/></svg>
              </div>
              <div className="v-footer-social" title="LinkedIn">
                <svg viewBox="0 0 24 24" fill="currentColor"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
              </div>
            </div>
          </div>
        </div>
      </footer>

      {/* ════════ BACK TO TOP ════════ */}
      <button
        className={`v-back-top${showBackTop ? ' show' : ''}`}
        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        aria-label="Retour en haut"
      >
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
          <path d="M10 16V4M10 4l-5 5M10 4l5 5" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </button>
    </div>
  );
}

function FeatureItem({ icon, title, desc, color }) {
  return (
    <div className="v-feature">
      <div className={`v-feature-icon v-feature-icon-${color}`}>
        <span style={{ fontSize: 16 }}>{icon}</span>
      </div>
      <div>
        <h4>{title}</h4>
        <p>{desc}</p>
      </div>
    </div>
  );
}

export default LandingPage;
